// Fixed SubscriptionDropIn.js - Request PaymentIntent for dropIn compatibility

import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { sendMessage } from "../services/websocketManager";

const SubscriptionDropIn = ({
  planId,
  amount,
  currency = "USD",
  env = "demo",
  onSuccess,
  onError,
  language = "en",
  userData,
  webSocketMessage,
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [isAirwallexReady, setIsAirwallexReady] = useState(false);
  const elementRef = useRef(null);
  const airwallexRef = useRef(null);
  const initStateRef = useRef({
    messageSent: false,
    currentPlanId: null,
    currentPlanType: null
  });
  
  const processedMessagesRef = useRef(new Set());
  const planType = "subscription";

  // Handle WebSocket messages
  useEffect(() => {
    if (!webSocketMessage) return;

    const messageType = webSocketMessage.messageType;
    const messageId = `${messageType}_${webSocketMessage.timestamp}`;
    
    if (processedMessagesRef.current.has(messageId)) {
      return;
    }
    
    const relevantMessages = [
      "subscription_setup_complete",
      "subscription_setup_failed",
      "subscription_created_success",
      "subscription_creation_failed",
      "airwallex_client_secret", // For PaymentIntent
      "error"
    ];
    
    if (!relevantMessages.includes(messageType)) {
      return;
    }

    console.log("📨 SubscriptionDropIn: Processing relevant message:", messageType);
    processedMessagesRef.current.add(messageId);

    switch (messageType) {
      case "subscription_setup_complete":
        console.log("✅ SubscriptionDropIn: Subscription setup complete received!");
        
        if (webSocketMessage.payment?.clientSecret) {
          const newClientSecret = webSocketMessage.payment.clientSecret;
          console.log("🔑 Setting client secret:", newClientSecret);
          
          // 🔥 CHECK: Determine if this is PaymentConsent or PaymentIntent
          try {
            // Decode the JWT to check the type
            const payload = JSON.parse(atob(newClientSecret.split('.')[1]));
            console.log("🔍 Client secret payload:", payload);
            
            if (payload.consent_id) {
              console.warn("⚠️ Received PaymentConsent client_secret, but we need PaymentIntent for dropIn");
              // Request PaymentIntent instead
              requestPaymentIntent();
              return;
            }
          } catch (e) {
            console.log("🔍 Could not decode client_secret, proceeding with standard approach");
          }
          
          if (clientSecret !== newClientSecret) {
            setClientSecret(newClientSecret);
            setIsInitializing(false);
            setError(null);
          }
        } else {
          console.error("❌ No client secret in subscription_setup_complete");
          setError("No client secret received");
          setIsInitializing(false);
        }
        break;
        
      case "airwallex_client_secret":
        console.log("✅ SubscriptionDropIn: PaymentIntent client secret received!");
        if (webSocketMessage.payment?.clientSecret) {
          const newClientSecret = webSocketMessage.payment.clientSecret;
          console.log("🔑 Setting PaymentIntent client secret:", newClientSecret);
          
          if (clientSecret !== newClientSecret) {
            setClientSecret(newClientSecret);
            setIsInitializing(false);
            setError(null);
          }
        }
        break;
        
      case "subscription_setup_failed":
        console.error("❌ SubscriptionDropIn: Subscription setup failed:", webSocketMessage);
        const errorMessage = webSocketMessage.message || webSocketMessage.error || "Subscription setup failed";
        setError(errorMessage);
        setIsInitializing(false);
        break;
        
      case "subscription_created_success":
        console.log("🎉 SubscriptionDropIn: Subscription created successfully!");
        if (onSuccess) {
          onSuccess(webSocketMessage);
        }
        break;
        
      case "subscription_creation_failed":
        console.error("❌ SubscriptionDropIn: Subscription creation failed:", webSocketMessage);
        if (onError) {
          onError(new Error(webSocketMessage.message || "Subscription creation failed"));
        }
        break;
        
      case "error":
        console.error("❌ SubscriptionDropIn: Error message:", webSocketMessage);
        setError(webSocketMessage.message || "An error occurred");
        setIsInitializing(false);
        break;
        
      default:
        break;
    }
  }, [webSocketMessage, onSuccess, onError, clientSecret]);

  const sendMessageWithConfirmation = useCallback((message) => {
    try {
      console.log("📤 SubscriptionDropIn: Sending message:", message.messageType);
      sendMessage(message);
      return true;
    } catch (error) {
      console.error("❌ Failed to send WebSocket message:", error);
      setError("Failed to send message to server");
      setIsInitializing(false);
      return false;
    }
  }, []);

  // 🔥 NEW: Function to request PaymentIntent for dropIn compatibility
  const requestPaymentIntent = useCallback(() => {
    console.log("📤 Requesting PaymentIntent for dropIn compatibility...");
    
    const msg = {
      ...generalMessageTemplate,
      timestamp: new Date().toISOString(),
      messageType: "create_payment_intent_for_subscription",
      payment: {
        currency: currency,
        planId: planId,
        planType: planType,
        amount: parseFloat(amount) * 100, // Convert to cents
        customer: {
          id: userData?.id || userData?.userId,
          email: userData?.email,
          name: userData?.name || userData?.displayName,
        },
        environment: env,
        requestId: `pi_sub_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        // 🔥 IMPORTANT: Indicate this is for subscription setup
        setupFutureUsage: true,
        paymentMethodTypes: ['card']
      }
    };
    
    sendMessageWithConfirmation(msg);
  }, [amount, currency, planId, planType, userData, env, sendMessageWithConfirmation]);

  // Send setup message
  useEffect(() => {
    const currentInitState = initStateRef.current;
    
    const shouldSendMessage = (
      !currentInitState.messageSent ||
      currentInitState.currentPlanId !== planId ||
      currentInitState.currentPlanType !== planType
    );

    if (!shouldSendMessage) {
      return;
    }

    console.log("🚀 SubscriptionDropIn: Initializing subscription setup...");
    setIsInitializing(true);
    setError(null);

    // 🔥 MODIFIED: Request PaymentIntent directly for better dropIn compatibility
    const msg = { 
      ...generalMessageTemplate,
      
      messageType: "create_payment_intent_for_subscription", // Changed message type
      payment: {
        currency: currency,
        planId: planId,
        planType: planType,
        amount: parseFloat(amount) * 100, // Convert to cents for Airwallex
       
        // 🔥 IMPORTANT: For subscription setup
        setupFutureUsage: true,
        paymentMethodTypes: ['card']
      }
    };
    
    const sent = sendMessageWithConfirmation(msg);
    
    if (sent) {
      initStateRef.current = {
        messageSent: true,
        currentPlanId: planId,
        currentPlanType: planType
      };
    }
  }, [planId, planType, currency, env, userData, amount, sendMessageWithConfirmation]);

  // 🔥 FIXED: Standard dropIn with PaymentIntent
  useEffect(() => {
    if (!clientSecret || airwallexRef.current) {
      return;
    }

    console.log("🔧 SubscriptionDropIn: Initializing Airwallex dropIn with PaymentIntent");

    const initializeAirwallex = async () => {
      try {
        // Load Airwallex SDK
        if (!window.Airwallex) {
          console.log("📦 Loading Airwallex SDK...");
          const script = document.createElement("script");
          script.src = "https://checkout.airwallex.com/assets/elements.bundle.min.js";
          document.head.appendChild(script);
          
          await new Promise((resolve, reject) => {
            script.onload = () => {
              console.log("✅ Airwallex SDK loaded");
              resolve();
            };
            script.onerror = reject;
          });
        }

        // Initialize Airwallex
        await window.Airwallex.init({
          env: env,
          origin: window.location.origin,
        });
        console.log("✅ Airwallex initialized");

        if (!elementRef.current) {
          throw new Error("Element container not found");
        }

        // Clear container
        elementRef.current.innerHTML = '';

        // 🔥 STANDARD: dropIn with PaymentIntent (should show complete form)
        console.log("🎨 Creating standard Airwallex dropIn with PaymentIntent...");
        const element = window.Airwallex.createElement("dropIn", {
          intent: {
            client_secret: clientSecret,
          },
          currency: currency,
          locale: language || 'en',
          appearance: {
            theme: "default",
            variables: {
              colorPrimary: '#1976d2',
              borderRadius: '8px',
              spacingUnit: '16px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '16px'
            }
          }
        });

        console.log("🚀 Mounting standard dropIn element...");
        element.mount(elementRef.current);
        airwallexRef.current = element;

        // Standard event handlers
        element.on("ready", () => {
          console.log("✅ Standard dropIn element ready - complete form should be visible");
          setIsAirwallexReady(true);
          
          // Verify form content
          setTimeout(() => {
            const container = elementRef.current;
            if (container) {
              const formElements = container.querySelectorAll('input, button, iframe, [role="textbox"]');
              console.log(`🔍 Found ${formElements.length} form elements in container`);
              
              if (formElements.length > 0) {
                console.log("✅ Payment form elements detected successfully");
              } else {
                console.warn("⚠️ No form elements found after mounting");
              }
            }
          }, 1000);
        });

        element.on("error", (event) => {
          console.error("❌ Standard dropIn error:", event);
          setError("Payment form error: " + (event.error?.message || event.message || "Unknown error"));
          setIsAirwallexReady(false);
        });

        element.on("success", (result) => {
          console.log("🎉 Payment successful:", result);
          console.log("🔍 Payment result details:", JSON.stringify(result, null, 2));
          
          // For subscriptions with PaymentIntent, create subscription after successful payment
          const subscriptionMsg = { 
            ...generalMessageTemplate,
            messageType: "create_subscription_after_payment",
            payment: {
              planId: planId,
              paymentIntentId: result.intent?.id || result.id,
              paymentMethodId: result.payment_method?.id,
              setupResult: result
            }
          };
          
          console.log("📤 Creating subscription after successful payment");
          sendMessage(subscriptionMsg);
          
          if (onSuccess) onSuccess(result);
        });

        console.log("✅ Standard dropIn setup complete");

      } catch (error) {
        console.error("❌ Failed to initialize standard dropIn:", error);
        setError("Failed to initialize payment form: " + error.message);
        setIsAirwallexReady(false);
      }
    };

    initializeAirwallex();
  }, [clientSecret, env, language, currency, planId, onSuccess]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (airwallexRef.current) {
        try {
          airwallexRef.current.destroy();
          airwallexRef.current = null;
        } catch (error) {
          console.warn("⚠️ Error destroying Airwallex element:", error);
        }
      }
    };
  }, []);

  if (error) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="error" variant="h6">
          Payment Setup Failed
        </Typography>
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (isInitializing || !clientSecret) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography variant="body1">
          Setting up your subscription payment...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "gray" }}>
          Preparing payment form for ${amount}/month subscription
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, textAlign: "center", color: "#1976d2" }}>
        Complete Your Subscription Payment
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, textAlign: "center", color: "gray" }}>
        Monthly subscription: ${amount} • Complete payment to activate
      </Typography>
      
      {/* Loading overlay until Airwallex is ready */}
      {!isAirwallexReady && clientSecret && (
        <Box sx={{ 
          position: "absolute", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)",
          zIndex: 5,
          backgroundColor: "rgba(255,255,255,0.95)",
          padding: 3,
          borderRadius: 2,
          boxShadow: 2
        }}>
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              Loading payment form...
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Payment form container */}
      <Box
        ref={elementRef}
        sx={{
          minHeight: "400px",
          width: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
          padding: "24px",
          backgroundColor: "#ffffff",
          position: "relative"
        }}
      />
      
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography variant="caption" sx={{ color: "gray", display: "block" }}>
          🔒 Your payment information is encrypted and secure
        </Typography>
        <Typography variant="caption" sx={{ color: "gray", display: "block", mt: 0.5 }}>
          Your subscription will be activated immediately after payment
        </Typography>
      </Box>
    </Box>
  );
};

export default SubscriptionDropIn;